// Global Instructions Rule Applied!
//
// Deal Execution services — HTTP calls only (board read, checklist answer,
// stage history). Stage moves and reordering reuse the existing
// PIPELINE_DEALS.PATCH endpoint (same raw-object payload the origination
// kanban sends).
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  DealExecutionBoardRecord,
  DealExecutionContext,
  DealExecutionStageHistoryEntry,
  validateDealExecutionBoardRecord,
  validateDealExecutionContext,
  validateDealExecutionStageHistoryEntry
} from '../../../state_manager/data_models/deal_execution_board_model.ts'
import { CapTableData, validateCapTableData } from '../../../state_manager/data_models/cap_table_model.ts'
import { LegalRightsData, validateLegalRightsData } from '../../../state_manager/data_models/legal_rights_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

export interface ExecutionBoardResponse {
  data: DealExecutionBoardRecord[]
  error?: string
}

export const fetchExecutionBoard = async (
  accessToken: string | null,
  params: {
    pipelineId?: string | null
    dealTypeId?: string | null
    search?: string | null
    dealId?: string | null
  } = {}
): Promise<ExecutionBoardResponse> => {
  try {
    const requestBody = {
      data: {
        pipeline_id: params.pipelineId ?? null,
        deal_type_id: params.dealTypeId ?? null,
        search: params.search ?? null,
        deal_id: params.dealId ?? null
      }
    }
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.BOARD), {
      method: API_ROUTES.DEAL_EXECUTION.BOARD.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify(requestBody)
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Execution board request failed (${response.status})`)
    }
    const body = await response.json()
    const rows = (body.data || []) as DealExecutionBoardRecord[]
    validateDealExecutionBoardRecord(rows, { isArray: true })
    return { data: rows }
  } catch (e) {
    console.error('fetchExecutionBoard failed:', e)
    return { data: [], error: e instanceof Error ? e.message : String(e) }
  }
}

export interface ChecklistAnswerResponse {
  data: { id?: string; answer?: boolean; answered_by?: string | null; answered_at?: string | null } | null
  error?: string
}

export const setChecklistAnswer = async (
  accessToken: string | null,
  dealId: string,
  checklistItemId: string,
  answer: boolean
): Promise<ChecklistAnswerResponse> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.CHECKLIST_ANSWER),
      {
        method: API_ROUTES.DEAL_EXECUTION.CHECKLIST_ANSWER.method,
        headers: authHeaders(accessToken),
        body: JSON.stringify({
          data: { deal_id: dealId, checklist_item_id: checklistItemId, answer }
        })
      }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Checklist answer request failed (${response.status})`)
    }
    const body = await response.json()
    return { data: body.data || null }
  } catch (e) {
    console.error('setChecklistAnswer failed:', e)
    return { data: null, error: e instanceof Error ? e.message : String(e) }
  }
}

export interface StageHistoryResponse {
  data: DealExecutionStageHistoryEntry[]
  error?: string
}

export const fetchStageHistory = async (
  accessToken: string | null,
  dealId: string
): Promise<StageHistoryResponse> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.STAGE_HISTORY, {}, { deal_id: dealId }),
      { method: API_ROUTES.DEAL_EXECUTION.STAGE_HISTORY.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Stage history request failed (${response.status})`)
    }
    const body = await response.json()
    const rows = (body.data || []) as DealExecutionStageHistoryEntry[]
    validateDealExecutionStageHistoryEntry(rows, { isArray: true })
    return { data: rows }
  } catch (e) {
    console.error('fetchStageHistory failed:', e)
    return { data: [], error: e instanceof Error ? e.message : String(e) }
  }
}

export const fetchExecutionContext = async (
  accessToken: string | null,
  dealId: string
): Promise<{ data: DealExecutionContext | null; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.CONTEXT, {}, { deal_id: dealId }),
      { method: API_ROUTES.DEAL_EXECUTION.CONTEXT.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Execution context request failed (${response.status})`)
    }
    const body = await response.json()
    const context = (body.data || { round: null, transactions: [] }) as DealExecutionContext
    validateDealExecutionContext(context)
    return { data: context }
  } catch (e) {
    console.error('fetchExecutionContext failed:', e)
    return { data: null, error: e instanceof Error ? e.message : String(e) }
  }
}

export const enterExecution = async (
  accessToken: string | null,
  dealId: string
): Promise<{ success: boolean; status?: string; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.ENTER), {
      method: API_ROUTES.DEAL_EXECUTION.ENTER.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { deal_id: dealId } })
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Enter execution request failed (${response.status})`)
    }
    const body = await response.json()
    return { success: true, status: body.data?.status }
  } catch (e) {
    console.error('enterExecution failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

export interface AccountDealsRoundsData {
  rounds: Array<Record<string, unknown>>
  deals: Array<Record<string, unknown>>
}

export const fetchAccountDealsRounds = async (
  accessToken: string | null,
  accountId: string
): Promise<{ data: AccountDealsRoundsData; error?: string }> => {
  const empty: AccountDealsRoundsData = { rounds: [], deals: [] }
  try {
    const response = await fetch(
      buildFullUrl(
        BACKEND_BASE_URL,
        API_ROUTES.DEAL_EXECUTION.ACCOUNT_OVERVIEW,
        {},
        { account_id: accountId }
      ),
      { method: API_ROUTES.DEAL_EXECUTION.ACCOUNT_OVERVIEW.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Account deals & rounds request failed (${response.status})`)
    }
    const body = await response.json()
    return { data: { ...empty, ...(body.data || {}) } }
  } catch (e) {
    console.error('fetchAccountDealsRounds failed:', e)
    return { data: empty, error: e instanceof Error ? e.message : String(e) }
  }
}

export interface DealDocumentsData {
  // Categories own types (cfg.document_categories -> cfg.document_types), so a
  // file's category is derived from its type rather than stored on the file.
  document_categories: Array<{ id: string; label: string }>
  document_types: Array<{
    id: string
    label: string
    category_id?: string | null
    category_label?: string | null
    /** false = the type no longer classifies new files; hidden from the pick
     *  list but still labels a file already classified with it. */
    active?: boolean
  }>
  files: Array<Record<string, unknown>>
  requirements: Array<Record<string, unknown>>
}

export const fetchDealDocuments = async (
  accessToken: string | null,
  dealId: string
): Promise<{ data: DealDocumentsData; error?: string }> => {
  const empty: DealDocumentsData = { document_categories: [], document_types: [], files: [], requirements: [] }
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.DOCUMENTS, {}, { deal_id: dealId }),
      { method: API_ROUTES.DEAL_EXECUTION.DOCUMENTS.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Deal documents request failed (${response.status})`)
    }
    const body = await response.json()
    return { data: { ...empty, ...(body.data || {}) } }
  } catch (e) {
    console.error('fetchDealDocuments failed:', e)
    return { data: empty, error: e instanceof Error ? e.message : String(e) }
  }
}

export const classifyDealFile = async (
  accessToken: string | null,
  fileId: string,
  fields: { document_type_id?: string | null; lifecycle?: string | null; version?: number | null }
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.DOCUMENTS_CLASSIFY),
      {
        method: API_ROUTES.DEAL_EXECUTION.DOCUMENTS_CLASSIFY.method,
        headers: authHeaders(accessToken),
        body: JSON.stringify({ data: { file_id: fileId, ...fields } })
      }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `File classify failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('classifyDealFile failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

export const saveRoundInstrument = async (
  accessToken: string | null,
  roundId: string,
  instrumentId: string | null,
  fields: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.ROUND_INSTRUMENT_SAVE),
      {
        method: API_ROUTES.DEAL_EXECUTION.ROUND_INSTRUMENT_SAVE.method,
        headers: authHeaders(accessToken),
        body: JSON.stringify({ data: { round_id: roundId, instrument_id: instrumentId, fields } })
      }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Round instrument save failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('saveRoundInstrument failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

export const deleteRoundInstrument = async (
  accessToken: string | null,
  instrumentId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(
        BACKEND_BASE_URL,
        API_ROUTES.DEAL_EXECUTION.ROUND_INSTRUMENT_DELETE,
        {},
        { instrument_id: instrumentId }
      ),
      {
        method: API_ROUTES.DEAL_EXECUTION.ROUND_INSTRUMENT_DELETE.method,
        headers: authHeaders(accessToken)
      }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Round instrument delete failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('deleteRoundInstrument failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

export const saveRoundParticipation = async (
  accessToken: string | null,
  roundId: string,
  participationId: string | null,
  fields: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.ROUND_PARTICIPATION_SAVE),
      {
        method: API_ROUTES.DEAL_EXECUTION.ROUND_PARTICIPATION_SAVE.method,
        headers: authHeaders(accessToken),
        body: JSON.stringify({
          data: { round_id: roundId, participation_id: participationId, fields }
        })
      }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Round participation save failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('saveRoundParticipation failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

export const deleteRoundParticipation = async (
  accessToken: string | null,
  participationId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(
        BACKEND_BASE_URL,
        API_ROUTES.DEAL_EXECUTION.ROUND_PARTICIPATION_DELETE,
        {},
        { participation_id: participationId }
      ),
      {
        method: API_ROUTES.DEAL_EXECUTION.ROUND_PARTICIPATION_DELETE.method,
        headers: authHeaders(accessToken)
      }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Round participation delete failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('deleteRoundParticipation failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

export interface WireInstructionsData {
  authorised: boolean
  own: Record<string, unknown> | null
  suggested: Record<string, unknown> | null
  /** Company-derived prefills (beneficiary / address) — lowest precedence */
  defaults: Record<string, unknown> | null
}

export const fetchWireInstructions = async (
  accessToken: string | null,
  dealId: string
): Promise<{ data: WireInstructionsData; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.WIRE_GET, {}, { deal_id: dealId }),
      { method: API_ROUTES.DEAL_EXECUTION.WIRE_GET.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Wire instructions request failed (${response.status})`)
    }
    const body = await response.json()
    return { data: body.data || { authorised: false, own: null, suggested: null, defaults: null } }
  } catch (e) {
    console.error('fetchWireInstructions failed:', e)
    return {
      data: { authorised: false, own: null, suggested: null, defaults: null },
      error: e instanceof Error ? e.message : String(e)
    }
  }
}

export const saveWireInstructions = async (
  accessToken: string | null,
  dealId: string,
  fields: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.WIRE_SAVE), {
      method: API_ROUTES.DEAL_EXECUTION.WIRE_SAVE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { deal_id: dealId, fields } })
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Wire save failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('saveWireInstructions failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

/**
 * PATCH /rounds — bare round columns ({id, ...fields}), rounds-model validated.
 * `data` is the response body as the server sends it - {data: {search_field:
 * 'rounds__id', search_value, data: {rounds__*}}} - ready for
 * syncUpdatesToState, as InputManager passes it.
 */
export const patchRound = async (
  accessToken: string | null,
  updates: { id: string } & Record<string, unknown>
): Promise<{ success: boolean; data?: Record<string, unknown>; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ROUNDS.PATCH), {
      method: API_ROUTES.ROUNDS.PATCH.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify(updates)
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.message || body.error || `Round patch failed (${response.status})`)
    }
    const body = await response.json().catch(() => null)
    return { success: true, data: body ?? undefined }
  } catch (e) {
    console.error('patchRound failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

/** PATCH /deals — used here to link/unlink the deal's round ({id, round}). */
export const patchDealRound = async (
  accessToken: string | null,
  dealId: string,
  roundId: string | null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEALS.PATCH), {
      method: API_ROUTES.DEALS.PATCH.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ id: dealId, round: roundId })
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.message || body.error || `Deal round link failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('patchDealRound failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

/** POST /deal-execution/move-stage — atomic Next/Back: stage + stage_date +
 *  a sequence placing the deal at the TOP of the target column. */
export const moveExecutionStage = async (
  accessToken: string | null,
  pipelineDealId: string,
  stage: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.MOVE_STAGE), {
      method: API_ROUTES.DEAL_EXECUTION.MOVE_STAGE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { pipeline_deal_id: pipelineDealId, stage } })
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.message || body.error || `Stage move failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('moveExecutionStage failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

export const patchPipelineDeal = async (
  accessToken: string | null,
  updates: { id: string; sequence?: number; stage?: string; stage_date?: string }
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PIPELINE_DEALS.PATCH), {
      method: API_ROUTES.PIPELINE_DEALS.PATCH.method,
      headers: authHeaders(accessToken),
      // NB: this endpoint takes the raw object, not the {data:{}} envelope —
      // matching the origination kanban's existing call.
      body: JSON.stringify(updates)
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Pipeline deal patch failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('patchPipelineDeal failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

// ============================================================================
// Cap table (Phase 4)
// ============================================================================

export interface CapTableSaveLinePayload {
  id?: string
  // Many-to-many: one class line may cover several securities.
  security_ids?: string[]
  class_name: string
  issued?: number | null
  fully_diluted?: number | null
  issue_price?: number | null
  is_esop_pool?: boolean
  line_kind?: 'equity' | 'warrant' | 'convertible' | 'debt'
  sort_order?: number
  holders: Array<{
    id?: string
    holder_kind: 'account' | 'text' | 'other'
    holder_account_id?: string | null
    holder_name?: string | null
    issued?: number | null
    fully_diluted?: number | null
    sort_order?: number
  }>
}

export interface CapTableSavePayload {
  id?: string
  account_id?: string
  round_id?: string | null
  kind?: string
  as_of_date?: string
  notes?: string | null
  lines: CapTableSaveLinePayload[]
}

export const fetchCapTable = async (
  accessToken: string | null,
  accountId: string,
  versionId: string | null = null,
  asOf: string | null = null
): Promise<{ data: CapTableData | null; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(
        BACKEND_BASE_URL,
        API_ROUTES.DEAL_EXECUTION.CAP_TABLE_GET,
        {},
        {
          account_id: accountId,
          ...(versionId ? { version_id: versionId } : {}),
          ...(asOf ? { as_of: asOf } : {})
        }
      ),
      { method: API_ROUTES.DEAL_EXECUTION.CAP_TABLE_GET.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Cap table request failed (${response.status})`)
    }
    const body = await response.json()
    return { data: validateCapTableData(body.data ?? {}) }
  } catch (e) {
    console.error('fetchCapTable failed:', e)
    return { data: null, error: e instanceof Error ? e.message : String(e) }
  }
}

export const saveCapTableVersion = async (
  accessToken: string | null,
  payload: CapTableSavePayload
): Promise<{ versionId: string | null; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.CAP_TABLE_SAVE), {
      method: API_ROUTES.DEAL_EXECUTION.CAP_TABLE_SAVE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Cap-table save failed (${response.status})`)
    }
    const body = await response.json()
    return { versionId: (body.data || {}).version_id ?? null }
  } catch (e) {
    console.error('saveCapTableVersion failed:', e)
    return { versionId: null, error: e instanceof Error ? e.message : String(e) }
  }
}

export const setCapTableCurrent = async (
  accessToken: string | null,
  versionId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.CAP_TABLE_SET_CURRENT),
      {
        method: API_ROUTES.DEAL_EXECUTION.CAP_TABLE_SET_CURRENT.method,
        headers: authHeaders(accessToken),
        body: JSON.stringify({ data: { version_id: versionId } })
      }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Set-current failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('setCapTableCurrent failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

export const createCapTableSplit = async (
  accessToken: string | null,
  params: { accountId: string; factor: number; asOf: string; notes?: string | null }
): Promise<{ versionId: string | null; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.CAP_TABLE_SPLIT), {
      method: API_ROUTES.DEAL_EXECUTION.CAP_TABLE_SPLIT.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({
        data: {
          account_id: params.accountId,
          factor: params.factor,
          as_of: params.asOf,
          notes: params.notes ?? null
        }
      })
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Split failed (${response.status})`)
    }
    const body = await response.json()
    return { versionId: (body.data || {}).version_id ?? null }
  } catch (e) {
    console.error('createCapTableSplit failed:', e)
    return { versionId: null, error: e instanceof Error ? e.message : String(e) }
  }
}

export const deleteCapTableVersion = async (
  accessToken: string | null,
  versionId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(
        BACKEND_BASE_URL,
        API_ROUTES.DEAL_EXECUTION.CAP_TABLE_DELETE,
        {},
        { version_id: versionId }
      ),
      { method: API_ROUTES.DEAL_EXECUTION.CAP_TABLE_DELETE.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Cap-table version delete failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('deleteCapTableVersion failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

export type CoInvestmentRow = Record<string, unknown>

export const fetchAccountCoInvestments = async (
  accessToken: string | null,
  accountId: string
): Promise<{ data: CoInvestmentRow[]; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(
        BACKEND_BASE_URL,
        API_ROUTES.DEAL_EXECUTION.CO_INVESTMENTS,
        {},
        { account_id: accountId }
      ),
      { method: API_ROUTES.DEAL_EXECUTION.CO_INVESTMENTS.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Co-investments request failed (${response.status})`)
    }
    const body = await response.json()
    return { data: Array.isArray(body.data) ? body.data : [] }
  } catch (e) {
    console.error('fetchAccountCoInvestments failed:', e)
    return { data: [], error: e instanceof Error ? e.message : String(e) }
  }
}

// ---------------------------------------------------------------------------
// Legal rights (Phase 5) — agreement sets + structured rights + evaluator
// ---------------------------------------------------------------------------
export interface LegalRightSavePayload {
  id?: string
  right_type_id?: string | null
  name: string
  basis: 'absolute' | 'rule' | 'right_reference'
  rule_variable?: string | null
  rule_operator?: string | null
  rule_threshold?: number | null
  referenced_right_id?: string | null
  reference_text?: string | null
  carried?: boolean
  effective_from?: string | null
  end_date?: string | null
  section_ref?: string | null
  source_file_id?: string | null
  source_url?: string | null
  notes?: string | null
  sort_order?: number
}

export interface LegalSetSavePayload {
  id?: string
  account_id?: string
  round_id?: string | null
  deal_id?: string | null
  name: string
  executed_date?: string | null
  notes?: string | null
  rights: LegalRightSavePayload[]
}

export const fetchLegalRights = async (
  accessToken: string | null,
  accountId: string
): Promise<{ data: LegalRightsData | null; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(
        BACKEND_BASE_URL,
        API_ROUTES.DEAL_EXECUTION.LEGAL_RIGHTS_GET,
        {},
        { account_id: accountId }
      ),
      { method: API_ROUTES.DEAL_EXECUTION.LEGAL_RIGHTS_GET.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Legal rights request failed (${response.status})`)
    }
    const body = await response.json()
    return { data: validateLegalRightsData(body.data ?? {}) }
  } catch (e) {
    console.error('fetchLegalRights failed:', e)
    return { data: null, error: e instanceof Error ? e.message : String(e) }
  }
}

export const saveLegalSet = async (
  accessToken: string | null,
  payload: LegalSetSavePayload
): Promise<{ setId: string | null; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.LEGAL_SET_SAVE), {
      method: API_ROUTES.DEAL_EXECUTION.LEGAL_SET_SAVE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Agreement-set save failed (${response.status})`)
    }
    const body = await response.json()
    return { setId: (body.data || {}).set_id ?? null }
  } catch (e) {
    console.error('saveLegalSet failed:', e)
    return { setId: null, error: e instanceof Error ? e.message : String(e) }
  }
}

export const setLegalSetStatus = async (
  accessToken: string | null,
  params: {
    setId: string
    action: 'execute' | 'revert_to_draft'
    executedDate?: string | null
    supersedes?: string[] | null
  }
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_EXECUTION.LEGAL_SET_STATUS),
      {
        method: API_ROUTES.DEAL_EXECUTION.LEGAL_SET_STATUS.method,
        headers: authHeaders(accessToken),
        body: JSON.stringify({
          data: {
            set_id: params.setId,
            action: params.action,
            executed_date: params.executedDate ?? null,
            supersedes: params.supersedes ?? null
          }
        })
      }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Status change failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('setLegalSetStatus failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

export const deleteLegalSet = async (
  accessToken: string | null,
  setId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(
        BACKEND_BASE_URL,
        API_ROUTES.DEAL_EXECUTION.LEGAL_SET_DELETE,
        {},
        { set_id: setId }
      ),
      { method: API_ROUTES.DEAL_EXECUTION.LEGAL_SET_DELETE.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Agreement-set delete failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('deleteLegalSet failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}
